<template>

  <div>
    <div
      v-for="member in connections"
      :key="member.user_id_1"
      class="conn-card"
    >
      <div class="conn-card__avatar-wrap">
        <router-link :to="'/member/' + member.user_id_1 + '?from=/community'">
          <img
            :src="member.profile_image || 'https://media.iamconnected.com/profile_images/default.png'"
            class="conn-card__avatar"
            alt=""
          />
        </router-link>
        <span
          class="conn-card__dot"
          :class="member.is_available ? 'conn-card__dot--on' : 'conn-card__dot--off'"
          :title="member.is_available ? 'Available' : 'Unavailable'"
        ></span>
      </div>
      <div class="conn-card__info">
        <router-link
          :to="`/member/${member.id}?from=/community`"
          class="conn-card__name"
        >{{ member.full_name || (member.first_name + ' ' + member.last_name) }}</router-link>
        <span class="conn-card__status-label">{{ member.is_available ? 'Available' : 'Unavailable' }}</span>
      </div>

      <div class="member-row-actions">
        <b-button
          v-if="member.connection_status === 'connected'"
          size="sm"
          class="btn-gold"
          v-b-tooltip.hover="{ title: 'Request a process', disabled: !isSmallScreen }"
          @click="openRequestModal(member)"
        >
          <i class="fas fa-hands-helping mr-1"></i><span class="btn-text"> Request a process</span>
        </b-button>

        <template v-else-if="member.connection_status === 'pending'">
              <span
                v-if="!hideConnect"
                v-b-tooltip.hover="'Connection request is pending'"
                class="d-inline-block"
              >
                <b-button
                  size="sm"
                  disabled
                  class="btn-pending"
                  style="pointer-events: none;"
                >
                  <i class="fas fa-clock mr-1"></i> Pending
                </b-button>
              </span>
          <b-button
            size="sm"
            class="btn-gold"
            v-b-tooltip.hover="{ title: 'Request a process', disabled: !isSmallScreen }"
            @click="openRequestModal(member)"
          >
            <i class="fas fa-hands-helping mr-1"></i><span class="btn-text"> Request a process</span>
          </b-button>
        </template>

        <template v-else-if="member.connection_status === 'pending_incoming'">
          <b-button size="sm" class="btn-gold mr-1" @click="acceptRequest(member)">
            <i class="fas fa-check mr-1"></i> Accept
          </b-button>
          <b-button size="sm" variant="outline-secondary" @click="declineRequest(member)">
            <i class="fas fa-times mr-1"></i> Decline
          </b-button>
        </template>

        <b-button
          v-else-if="member.connection_status === 'declined'"
          size="sm"
          variant="outline-secondary"
          disabled
        >
          <i class="fas fa-ban mr-1"></i> Declined
        </b-button>

        <template v-else>
          <b-button
            v-if="!hideConnect"
            size="sm"
            class="btn-gold"
            v-b-tooltip.hover="{ title: 'Connect', disabled: !isSmallScreen }"
            @click="connectRequest(member)"
          >
            <i class="fas fa-user-plus mr-1"></i><span class="btn-text"> Connect</span>
          </b-button>
          <b-button
            size="sm"
            class="btn-gold"
            v-b-tooltip.hover="{ title: 'Request a process', disabled: !isSmallScreen }"
            @click="openRequestModal(member)"
          >
            <i class="fas fa-hands-helping mr-1"></i><span class="btn-text"> Request a process</span>
          </b-button>
        </template>
      </div>
    </div>


    <process-request-popup
      :requestTarget="requestTarget"
      :contractionId="$route.query.contraction_id || null"
      v-model="showRequestModal"
      v-if="showRequestModal"
    ></process-request-popup>

  </div>

</template>

<script>

import ProcessRequestPopup from "@/components/Processing/ProcessRequestPopup.vue";
import axios from "@/axios";

export default {
  components: {ProcessRequestPopup},
  props: {
    connections: {
      type: Array,
      default: Array,
    },
    loading: {
      type: Boolean,
    },
    title: {
      type: String,
      default: 'Available to hold space right now',
    },
    hideConnect: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showRequestModal: false,
      requestTarget: null,
      windowWidth: window.innerWidth,
      searchQuery: '',
    }
  },
  computed: {
    isSmallScreen() {
      return this.windowWidth <= 576;
    },
  },
  mounted() {
    this._resizeHandler = () => { this.windowWidth = window.innerWidth; };
    window.addEventListener('resize', this._resizeHandler);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this._resizeHandler);
  },

  methods: {
    openRequestModal(member) {
      this.requestTarget = member;
      this.showRequestModal = true;
    },
    async connectRequest(member) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Send a connection request to ${member.full_name}?`,
        { title: 'Connect', okTitle: 'Send Request', cancelTitle: 'Cancel', centered: true }
      );
      if (!confirmed) {
        return
      }

      member.connection_status = 'pending';

      try {
        await axios({
          url: 'connections',
          method: 'POST',
          params: { target_user_id: member.id },
        });
      } catch (err) {
        console.error('Connection request error:', err);
        member.connection_status = 'none';
      }
    },

    async acceptRequest(member) {
      member.connection_status = 'connected';

      try {
        await axios({
          url: 'connectionAccept',
          params: { sender_id: member.id },
        });
      } catch (err) {
        console.error('Accept error:', err);
        member.connection_status = 'pending_incoming';
      }
    },
    async declineRequest(member) {
      member.connection_status = 'declined';

      try {
        await axios({
          url: 'connectionDecline',
          params: { sender_id: member.id },
        });
      } catch (err) {
        console.error('Decline error:', err);
        member.connection_status = 'pending_incoming';
      }
    }

  }
}
</script>

<style scoped>
/* ── Dynamic card sections ── */
.card-columns { column-count: 1; }
@media screen and (min-width: 768px)  { .card-columns { column-count: 2; } }
@media screen and (min-width: 1050px) { .card-columns { column-count: 3; } }

.card-header .btn-sm { padding: 0 5px; margin: 0; }



.conn-card {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  background: #fff;
  border: 1px solid rgba(214,182,116,0.18);
  border-radius: 12px;
  padding: 12px 16px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  transition: transform 0.15s ease, box-shadow 0.18s ease;
  position: relative;
  overflow: hidden;
}

.conn-card::before {
  content: '';
  position: absolute;
  left: 0; top: 0; bottom: 0;
  width: 3px;
  border-radius: 12px 0 0 12px;
  background: linear-gradient(180deg, #d6b674 0%, #c4a55a 100%);
}

.conn-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(214,182,116,0.18);
}

/* Avatar */
.conn-card__avatar-wrap {
  position: relative;
  flex-shrink: 0;
}

.conn-card__avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(214,182,116,0.3);
  display: block;
  transition: border-color 0.2s;
}

.conn-card:hover .conn-card__avatar {
  border-color: #d6b674;
}

.conn-card__dot {
  position: absolute;
  bottom: 1px;
  right: 1px;
  width: 11px;
  height: 11px;
  border-radius: 50%;
  border: 2px solid #fff;
}

.conn-card__dot--on  { background: #28a745; }
.conn-card__dot--off { background: #ccc; }

/* Info */
.conn-card__info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.conn-card__name {
  font-weight: 600;
  font-size: 0.88rem;
  color: #333;
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  transition: color 0.18s;
}

.conn-card__name:hover {
  color: #d6b674;
  text-decoration: none;
}

.conn-card__status-label {
  font-size: 0.7rem;
  color: #aaa;
}

/* Gold button */
.btn-gold {
  background: #fff !important;
  color: #d6b674 !important;
  border: 1px solid #d6b674 !important;
  border-radius: 6px;
}
.btn-gold:hover, .btn-gold:focus, .btn-gold:active {
  background: linear-gradient(135deg, #d6b674 0%, #c4a55a 100%) !important;
  color: #fff !important;
  border-color: #d6b674 !important;
}

@media (max-width: 576px) {
  .btn-gold {
    font-size: 0.7rem !important;
    padding: 3px 7px !important;
  }
  .btn-gold .btn-text {
    display: none;
  }
}


/* Pending button */
.btn-pending {
  background: rgba(214,182,116,0.12) !important;
  border: 1px solid rgba(214,182,116,0.45) !important;
  color: #b3944b !important;
  border-radius: 20px;
  font-size: 0.75rem;
  cursor: default;
  opacity: 1 !important;
  white-space: nowrap;
}

.member-row-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  flex-wrap: nowrap;
  gap: 4px;
}

</style>
